import React, { useState, useEffect } from "react";
import { Plus, Search, X, IndianRupee } from "lucide-react";
import { formatCurrency, formatDate } from "../utils/mockData";
import { expensesApi } from "../utils/api";

const categories = ["Decoration", "Sound & Light", "Idol", "Prasad", "Cultural", "Printing", "Pooja Items", "Security", "Electricity", "Miscellaneous"];

function ExpenseModal({ onClose, onSave }) {
  const [form, setForm] = useState({ category: "Decoration", description: "", amount: "", vendor: "", billNo: "", date: new Date().toISOString().split("T")[0] });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.description.trim()) e.description = "Description required";
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) e.amount = "Valid amount required";
    if (!form.vendor.trim()) e.vendor = "Vendor required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({ ...form, id: `EXP-${Date.now()}`, amount: Number(form.amount) });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl z-10 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-base font-bold text-gray-900">Add Expense</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                {categories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Description *</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Expense description"
              className={`w-full border ${errors.description ? "border-red-400" : "border-gray-200"} rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400`} />
            {errors.description && <p className="text-xs text-red-500 mt-0.5">{errors.description}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Amount (₹) *</label>
              <div className="relative">
                <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0" min="1"
                  className={`w-full border ${errors.amount ? "border-red-400" : "border-gray-200"} rounded-xl pl-8 pr-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400`} />
              </div>
              {errors.amount && <p className="text-xs text-red-500 mt-0.5">{errors.amount}</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Bill No.</label>
              <input type="text" value={form.billNo} onChange={(e) => setForm({ ...form, billNo: e.target.value })}
                placeholder="B-001"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Vendor *</label>
            <input type="text" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              placeholder="Vendor / Supplier name"
              className={`w-full border ${errors.vendor ? "border-red-400" : "border-gray-200"} rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400`} />
            {errors.vendor && <p className="text-xs text-red-500 mt-0.5">{errors.vendor}</p>}
          </div>
          <button type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-orange-500/20">
            Add Expense
          </button>
        </form>
      </div>
    </div>
  );
}

const categoryColors = {
  "Idol": "bg-orange-100 text-orange-700",
  "Decoration": "bg-pink-100 text-pink-700",
  "Sound & Light": "bg-yellow-100 text-yellow-700",
  "Prasad": "bg-green-100 text-green-700",
  "Cultural": "bg-purple-100 text-purple-700",
  "Printing": "bg-blue-100 text-blue-700",
  "Pooja Items": "bg-red-100 text-red-700",
  "Security": "bg-gray-100 text-gray-700",
  "Electricity": "bg-amber-100 text-amber-700",
  "Miscellaneous": "bg-slate-100 text-slate-700",
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    expensesApi.list()
      .then((res) => setExpenses(res.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = expenses
    .filter((e) => {
      const matchSearch = e.description.toLowerCase().includes(search.toLowerCase()) ||
        e.vendor.toLowerCase().includes(search.toLowerCase());
      const matchCat = filterCat === "All" || e.category === filterCat;
      return matchSearch && matchCat;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleSave = async (expense) => {
    try {
      const saved = await expensesApi.create(expense);
      setExpenses([saved.item || expense, ...expenses]);
    } catch {
      setExpenses([expense, ...expenses]);
    }
    setShowModal(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 space-y-4 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-500">{expenses.length} entries</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-orange-500/20 text-sm">
          <Plus size={16} /> Add Expense
        </button>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
        <p className="text-xs text-gray-500 font-medium mb-1">Total Expenses</p>
        <p className="text-lg font-black text-gray-900">{formatCurrency(expenses.reduce((s, e) => s + Number(e.amount || 0), 0))}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search expenses..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["All", ...categories.slice(0, 5)].map((c) => (
            <button key={c} onClick={() => setFilterCat(c)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                filterCat === c ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
              }`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Category</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Description</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Vendor</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Date</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Bill No.</th>
                <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${categoryColors[e.category] || "bg-gray-100 text-gray-600"}`}>
                      {e.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px]">
                    <p className="truncate">{e.description}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{e.vendor}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(e.date)}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-500">{e.billNo}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">{formatCurrency(Number(e.amount || 0))}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map((e) => (
            <div key={e.id} className="p-4">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${categoryColors[e.category] || "bg-gray-100 text-gray-600"}`}>
                    {e.category}
                  </span>
                  <p className="text-sm font-semibold text-gray-800 mt-1">{e.description}</p>
                  <p className="text-xs text-gray-400">{e.vendor} • {formatDate(e.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-base font-black text-gray-900">{formatCurrency(e.amount)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && <ExpenseModal onClose={() => setShowModal(false)} onSave={handleSave} />}
    </div>
  );
}
